import { tz } from '@date-fns/tz';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { addDays, startOfDay } from 'date-fns';
import { useState } from 'react';
import * as z from 'zod';

import { CreateSessionModal } from '#modules/finder/features/posts/components/Post/CreateSessionModal';
import { InviteSessionLine } from '#modules/finder/features/posts/components/Post/InviteFriendDialog/SessionLine';
import { RecommendedPlayers } from '#modules/finder/features/posts/components/Post/RecommendedPlayers';
import { getSeats } from '#modules/finder/features/posts/model/sessionSeats';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { Panel } from '#features/ui';

const SESSION = '22222222-2222-4222-8222-222222222222';
const POST = '11111111-1111-4111-8111-111111111111';
const GAME = '77777777-7777-4777-8777-777777777777';
const HOST = '44444444-4444-4444-8444-444444444444';
const FRIEND = '99999999-9999-4999-8999-999999999999';
const MATE = '55555555-5555-4555-8555-555555555555';
const OTHER = '66666666-6666-4666-8666-666666666666';
const schema = z.object({
  view: z.enum(['invite', 'create']).default('invite'),
  lang: z.enum(['ru', 'en']).default('ru'),
  discord: z.enum(['linked', 'unlinked']).default('unlinked'),
  role: z.enum(['host', 'member']).default('member'),
  timeFormat: z.enum(['TWELVE', 'TWENTY_FOUR']).default('TWENTY_FOUR'),
  scheduleState: z.enum(['normal', 'busy', 'full']).default('normal'),
  state: z
    .enum(['normal', 'empty', 'error', 'loading', 'full', 'sent', 'rejected', 'many'])
    .default('normal'),
});

export const Route = createFileRoute('/{-$locale}/_layout/preview/session-invite')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: zodValidator(schema),
  component: InvitePreviewRoute,
});

function InvitePreviewRoute() {
  const search = Route.useSearch();
  return (
    <Preview
      key={`${search.view}:${search.role}:${search.state}:${search.lang}:${search.discord}:${search.timeFormat}:${search.scheduleState}`}
      {...search}
    />
  );
}

function Preview({
  role,
  state,
  lang,
  view,
  discord,
  timeFormat,
  scheduleState,
}: z.infer<typeof schema>) {
  // Реальные скомпилированные каталоги, без изменения языковых настроек профиля для QA.
  const [i18n] = useState(() =>
    setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } })
  );
  const [open, setOpen] = useState(true);
  const startedAt = new Date(Date.now() + 86400000).toISOString();
  const expiredAt = new Date(Date.now() + 90000000).toISOString();
  const members = [
    { memberId: HOST, status: 'ACCEPTED' },
    { memberId: MATE, status: 'ACCEPTED' },
    ...(state === 'sent' ? [{ memberId: FRIEND, status: 'INVITED' }] : []),
    ...(state === 'rejected' ? [{ memberId: FRIEND, status: 'REJECTED' }] : []),
  ];
  const [client] = useState(() => {
    const value = new QueryClient();
    const queries = value.defaultQueryOptions.bind(value);
    const mutations = value.defaultMutationOptions.bind(value);
    value.setQueryDefaults(['oauth-redirect-link'], { initialData: '#preview-discord-link' });
    value.defaultQueryOptions = (options) => ({
      ...queries(options),
      enabled: false,
      staleTime: Infinity,
      refetchInterval: false,
      retry: false,
      queryFn: async () => {
        throw new Error('Isolated preview cannot access the API');
      },
    });
    // Изолированная dev-витрина: действия никогда не обращаются к API.
    value.defaultMutationOptions = (options) => ({
      ...mutations(options),
      mutationFn: async () => ({ __typename: 'Operation', status: 'SUCCESS' }),
      retry: false,
    });
    const friends =
      state === 'many'
        ? Array.from({ length: 20 }, (_, index) => ({
            userId: `preview-${index}`,
            user: { displayName: `Friend ${index + 1}` },
          }))
        : [
            { userId: FRIEND, user: { displayName: 'Alex_with_a_very_long_display_name' } },
            { userId: MATE, user: { displayName: 'Lily' } },
          ];
    if (state === 'error') {
      const query = value
        .getQueryCache()
        .build(value, { queryKey: ['follow-list', 'friends', null] });
      query.setState({
        status: 'error',
        error: new Error('Preview load failure'),
        fetchStatus: 'idle',
      });
    } else if (state !== 'loading') {
      value.setQueryData(['follow-list', 'friends', null], {
        pages: [{ items: state === 'empty' ? [] : friends, nextCursor: null }],
        pageParams: [null],
      });
    } else {
      const query = value
        .getQueryCache()
        .build(value, { queryKey: ['follow-list', 'friends', null] });
      query.setState({ status: 'pending', fetchStatus: 'fetching' });
    }
    value.setQueryData(['session-invite-link', SESSION], 'preview-only');
    value.setQueryData(['me'], {
      __typename: 'CurrentUser',
      id: HOST,
      timeFormat,
      timeZoneOffset: 3,
      oauth: { discord: discord === 'linked' ? { id: 'preview-discord' } : null },
    });
    value.setQueryData(
      ['games'],
      [{ __typename: 'Game', id: GAME, name: 'Apex Legends', providers: [] }]
    );
    value.setQueryData(['platforms'], []);
    const tomorrow = startOfDay(addDays(new Date(), 1), { in: tz('+03:00') }).getTime();
    value.setQueryData(['my-active-sessions'], { records: [] });
    value.setQueryData(
      ['post-session-slots', POST],
      scheduleState === 'normal'
        ? []
        : [
            {
              sessionId: SESSION,
              postId: POST,
              authorId: HOST,
              acceptedMembersCount: 2,
              pendingInvites: 0,
              start: tomorrow + (scheduleState === 'full' ? 0 : 18 * 3600000),
              end: tomorrow + (scheduleState === 'full' ? 24 : 20) * 3600000,
            },
          ]
    );
    value.setQueryData(['follow-states', [OTHER]], new Map());
    value.setQueryData(['recent-teammates', 'picker'], []);
    value.setQueryData(['recommended-players', GAME, HOST], {
      records: [
        {
          id: OTHER,
          gameId: GAME,
          userData: { id: OTHER, displayName: 'pathfinder_77' },
          platformsIds: [],
          languages: [],
          data: [],
        },
      ],
    });
    value.setQueryData(['playerAvailability', [OTHER], GAME], []);
    value.setQueryData(
      ['playerAvailability', friends.map((friend) => friend.userId).toSorted(), null],
      friends.map((friend) => ({
        userId: friend.userId,
        status: 'ONLINE',
        playingNow: false,
        rank: 'ONLINE',
        matchesGame: false,
      }))
    );
    return value;
  });
  const seats = getSeats({
    acceptedMembersCount: state === 'full' ? 3 : 2,
    teamLimit: 3,
    members,
    host: { id: HOST, displayName: 'Host' },
    hostKeyFallback: POST,
  });
  return (
    <I18nProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <Panel variant="container" p={4}>
          Dev preview · {role} · {state}
        </Panel>
        {view === 'create' ? (
          <CreateSessionModal
            postId={POST}
            post={{ gameId: GAME }}
            open={open}
            onOpenChange={setOpen}
            onSuccess={() => undefined}
          />
        ) : (
          <RecommendedPlayers
            gameId={GAME}
            postId={POST}
            sessionId={SESSION}
            excludeUserId={HOST}
            platforms={[]}
            postPlatformIds={[]}
            sessionMembers={members}
            freeSeats={seats.open}
            teamLimit={3}
            hostMic
            hostLanguageCodes={[]}
            hostSettingIds={[]}
            onSessionAction={() => undefined}
            card={false}
            listOpen={open}
            onListOpenChange={setOpen}
            peopleEnabled={role === 'host'}
            gameName="Apex Legends"
            sessionLine={
              <InviteSessionLine
                gameName="Apex Legends"
                startedAt={startedAt}
                expiredAt={expiredAt}
                status="UPCOMING"
                seats={seats}
              />
            }
          />
        )}
      </QueryClientProvider>
    </I18nProvider>
  );
}
